import type { Property } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { useLogger } from '~/composables/useLogger';
import { usePropertiesListing } from '~/composables/usePropertiesListing';

interface UseWishlistPropertiesListingOptions {
	/** Wishlist ID to fetch properties from */
	wishlistId?: string | Ref<string>;
	/** Whether to include H3 clusters in response */
	includeH3Clusters?: boolean;
	/** Additional filters to apply */
	filters?: Ref<Partial<PropertyFilters>>;
	/**
	 * Whether to load properties immediately on mount.
	 * Set to false if you want to trigger loading manually.
	 * @default true
	 */
	immediate?: boolean;
}

interface UseWishlistPropertiesListingReturn {
	properties: Ref<Property[]>;
	isLoading: Ref<boolean>;
	isEmpty: Ref<boolean>;
	currentPage: Ref<number>;
	totalCount: Ref<number>;
	loadWishlistProperties: () => Promise<void>;
	loadNextPage: () => Promise<void>;
}

/**
 * Composable to manage wishlist properties with pagination
 * Uses the standard properties list endpoint with wishlist filters
 *
 * PERFORMANCE NOTE: This composable is synchronous to avoid blocking SSR.
 * Properties are loaded lazily on client-side or when explicitly triggered.
 * Use the `immediate` option to control auto-loading behavior.
 */
export function useWishlistPropertiesListing(
	options: UseWishlistPropertiesListingOptions = {}
): UseWishlistPropertiesListingReturn {
	const logger = useLogger('WishlistPropertiesListing');
	const {
		wishlistId,
		includeH3Clusters = false,
		filters,
		immediate = true
	} = options;
	const { user } = useUserSession();
	const getCookieData = useCookieData();

	// Lazy-load repository to avoid blocking
	const nuxtPropertiesRepositoryPromise =
		import('~~/adapters/repositories/nuxt');

	const wishlistPropertiesDataLoader = async (page: number) => {
		const { nuxtPropertiesRepository } = await nuxtPropertiesRepositoryPromise;

		const currentWishlistId = unref(wishlistId);
		const wishlistFilters = {
			...(filters?.value || {}),
			page,
			perPage: 12,
			...(includeH3Clusters && { includeH3Clusters }),
			...(currentWishlistId &&
				currentWishlistId !== 'minha-colecao' && {
					wishlistId: currentWishlistId
				}),
			showHideProperties: true,
			showUnpublishedProperties: true,
			...(user.value?.id && { markUserWishlistsId: user.value.id })
		};

		const [result, repositoryError] = await nuxtPropertiesRepository.list(
			wishlistFilters,
			getCookieData()
		);

		if (repositoryError || !result) {
			const errorMessage =
				repositoryError?.message ?? 'No result from repository';
			logger.error('Error while fetching properties from repository', {
				source: 'useWishlistPropertiesListing',
				error_message: errorMessage
			});

			throw new Error(errorMessage);
		}

		return {
			properties: result.properties ?? [],
			hasMore:
				(result.pagination?.page ?? 1) < (result.pagination?.totalPages ?? 1),
			totalCount: result.pagination?.filteredCount ?? 0,
			totalPages: result.pagination?.totalPages ?? 0
		};
	};

	// Uses the generic composable with custom data loader
	const {
		properties,
		isLoading,
		isEmpty,
		currentPage,
		totalCount,
		loadProperties,
		loadNextPage,
		reset
	} = usePropertiesListing({
		dataLoader: wishlistPropertiesDataLoader
	});

	// Watch filters changes to refresh automatically
	let filtersWatcher: (() => void) | null = null;
	if (filters) {
		filtersWatcher = watch(
			filters,
			() => {
				reset();
				loadProperties();
			},
			{ deep: true }
		);
	}

	// Cleanup watcher on unmount
	onScopeDispose(() => {
		if (filtersWatcher) {
			filtersWatcher();
		}
	});

	// Trigger initial load on client-side if immediate is true
	// This is non-blocking and allows SSR to complete faster
	if (immediate && import.meta.client) {
		loadProperties();
	}

	// Wrapper function with better naming
	const loadWishlistProperties = async () => {
		await loadProperties();
	};

	return {
		properties,
		isLoading,
		isEmpty,
		currentPage,
		totalCount,
		loadWishlistProperties,
		loadNextPage
	};
}
